import { useQuery } from '@tanstack/react-query'
import { useRouteContext } from '@tanstack/react-router'
import { supabase } from '../auth/supabase'
import type { UserPreferences } from '../types/auth'

export async function fetchUserPreferences(
  userId: string,
): Promise<UserPreferences | null> {
  const { data, error } = await supabase
    .from('user_preferences')
    .select('*')
    .eq('user_id', userId)
    .single()

  if (error) {
    // If no row exists yet (PGRST116), return null instead of throwing
    if (error.code === 'PGRST116') {
      return null
    }
    throw error
  }

  return data
}

export function useUser() {
  const { session } = useRouteContext({ from: '__root__' })
  const user = session?.user ?? null

  const {
    data: preferences,
    error,
    isLoading: isLoadingPreferences,
    refetch,
  } = useQuery({
    queryKey: ['user-preferences', user?.id],
    queryFn: () => fetchUserPreferences(user!.id),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, err: any) => {
      if (err?.code === 'PGRST116') {
        return false
      }
      return failureCount < 3
    },
  })

  return {
    user,
    preferences: preferences ?? null,
    isLoading: isLoadingPreferences && !!user,
    error: error as Error | null,
    refetchPreferences: () => refetch(),
  }
}
